"use client";

// Stage 2 (de-vendor Phase 6): the single app-owned theme provider — a
// genuinely local implementation, not a controlled wrapper around
// @datasirpi/theme's own <ThemeProvider>.
//
// There used to be four independent writers of theme state (audit finding
// 09): the inline bootstrap <script> in layout.tsx (KEPT — see there, it's
// the only thing that can run before React hydrates and prevent a flash of
// the wrong theme); a hand-rolled writer in ThemeToggle.tsx (deleted — dead
// code); a SECOND, fully independent <ThemeProvider> nested inside
// Sidebar.tsx with its own state; and the vendor ThemeProvider itself,
// whose one actual job was writing `data-ds-mode`. This file now owns the
// mode state AND writes `data-ds-mode` itself.
//
// ── Phase 5.1 / decision D3: "system" is a real mode, and the default ────
//
// It was light-only-or-dark-only before, and the default was light. Both
// were wrong for this product. A SOC runs 24/7 — the same console is read
// in a bright morning office and a dimmed 3am room, frequently by different
// people on the same machine — and every other application those analysts
// use already follows the OS. Defaulting to light meant every dark-desktop
// user got one flash of a white screen on first load and then had to go
// find the switch.
//
// Three things make "system" real rather than a third label:
//
//   1. MODE vs RESOLVED MODE are now separate. `mode` is what the user
//      chose and what gets persisted ("system"); `resolvedMode` is the
//      light/dark that everything else consumes. Every CSS rule in this app
//      keys off `data-ds-mode`, which always carries the RESOLVED value —
//      no stylesheet needs to know "system" exists.
//   2. It LISTENS. A system mode that only samples the OS at page load is
//      a worse light/dark toggle, because it silently goes stale. The
//      matchMedia subscription below means the console follows the desktop
//      switching at sunset without a reload.
//   3. The bootstrap script in layout.tsx resolves it too, so first paint
//      is already correct. That script, `INITIAL_MODE` here, and the store
//      below must agree or the first frame flashes.
//
// ── Why external stores instead of useEffect ─────────────────────────────
// Both the persisted choice and the OS preference are external systems that
// differ between server and client. useSyncExternalStore is the API built
// for exactly that: it returns the server snapshot during SSR and the real
// value immediately after hydration, with no setState-in-an-effect and no
// extra render pass. The previous version read localStorage in an effect,
// which cost a render and tripped this repo's own lint rule.
import { createContext, useCallback, useContext, useEffect, useSyncExternalStore, type ReactNode } from "react";
import { flushSync } from "react-dom";

const STORAGE_KEY = "d2d-theme";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedMode = "light" | "dark";

/** Must match the bootstrap script's fallback in layout.tsx. */
const INITIAL_MODE: ThemeMode = "system";
const DARK_QUERY = "(prefers-color-scheme: dark)";

export interface ThemeContextValue {
  /** What the user picked. May be "system". */
  mode: ThemeMode;
  /** The light/dark actually in force. Never "system". */
  resolvedMode: ResolvedMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/* ── Store 1: the persisted choice ──────────────────────────────────────
   localStorage fires no event for writes made by THIS tab, so the store
   keeps its own cache and notifies subscribers on write. The `storage`
   event covers the other direction — another tab changing the theme — which
   is a genuine improvement: an analyst with the inbox open in three tabs
   used to have to switch each one. */
const modeListeners = new Set<() => void>();
let cachedMode: ThemeMode = INITIAL_MODE;
let cacheHydrated = false;

function parseMode(v: string | null): ThemeMode {
  return v === "light" || v === "dark" || v === "system" ? v : INITIAL_MODE;
}

function readStoredMode(): ThemeMode {
  try {
    return parseMode(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private mode, blocked storage, or a sandboxed iframe. The theme still
    // works for this tab; it just will not be remembered.
    return INITIAL_MODE;
  }
}

function emitMode() {
  modeListeners.forEach((l) => l());
}

function subscribeMode(cb: () => void) {
  if (!cacheHydrated) {
    cachedMode = readStoredMode();
    cacheHydrated = true;
  }
  modeListeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return;
    cachedMode = parseMode(e.newValue);
    emitMode();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    modeListeners.delete(cb);
    window.removeEventListener("storage", onStorage);
  };
}

const getModeSnapshot = (): ThemeMode => (cacheHydrated ? cachedMode : readStoredMode());
const getModeServerSnapshot = (): ThemeMode => INITIAL_MODE;

/* ── Store 2: what the OS currently prefers ─────────────────────────────
   Read live rather than sampled once, so "system" keeps meaning system. */
function subscribeSystem(cb: () => void) {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mq = window.matchMedia(DARK_QUERY);
  // addEventListener on MediaQueryList is the modern API; the optional call
  // guards the older addListener-only implementations without reaching for
  // the deprecated method.
  mq.addEventListener?.("change", cb);
  return () => mq.removeEventListener?.("change", cb);
}

const getSystemSnapshot = (): ResolvedMode => {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
};
// The server cannot know the OS preference. It renders light, and the
// bootstrap script has already painted the correct one before React runs —
// so the only thing that can briefly disagree is React state read during
// render (Sidebar's rail theme, the chart palette), which corrects on
// hydration. That trade predates this change and is unchanged by it.
const getSystemServerSnapshot = (): ResolvedMode => "light";

function writeMode(next: ThemeMode) {
  cachedMode = next;
  cacheHydrated = true;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* storage blocked — mode still applies for this tab */
  }
  emitMode();
}

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const mode = useSyncExternalStore(subscribeMode, getModeSnapshot, getModeServerSnapshot);
  const systemMode = useSyncExternalStore(subscribeSystem, getSystemSnapshot, getSystemServerSnapshot);
  const resolvedMode: ResolvedMode = mode === "system" ? systemMode : mode;

  // `data-ds-mode` is the one thing that must stay current on every change
  // (not just first paint, which the bootstrap script already handles) —
  // this was the vendor ThemeProvider's entire actual job. Every dark-mode
  // rule in this app keys off this exact attribute (see globals.css's
  // `@custom-variant dark` and every `[data-ds-mode="dark"]` selector).
  // It carries the RESOLVED mode, so the OS flipping while in system mode
  // repaints the app without any rule knowing "system" exists.
  //
  // The write is bracketed by `data-theme-switching`, which kills every
  // transition on the page for the length of one frame (see the rule of
  // that name in globals.css). Without it a toggle is visibly ragged rather
  // than instant, and the reason is a split, not a slowness: surfaces with
  // no colour transition — the sidebar rail, the topbar, section labels —
  // snap over immediately, while everything that DOES carry one (~50
  // transition rules here plus ~90 Tailwind transition-colors call sites)
  // crossfades for 150-200ms behind them. Measured in the browser: the rail
  // reached its final colour in 1 step, the row labels took 8, the body 12
  // — so dark ink sat on an already-dark rail through the middle of a flip.
  useEffect(() => {
    const root = typeof document !== "undefined" ? document.documentElement : null;
    if (!root) return;

    root.setAttribute("data-theme-switching", "");
    root.setAttribute("data-ds-mode", resolvedMode);
    // Kept in step with the bootstrap script, which writes all three.
    root.setAttribute("data-theme", resolvedMode);
    root.classList.toggle("dark", resolvedMode === "dark");

    // Two frames, not one: the first is when the browser recomputes style
    // with transitions suppressed (so the colours land), the second is when
    // handing them back can no longer start an animation from the old value.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => root.removeAttribute("data-theme-switching"));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      root.removeAttribute("data-theme-switching");
    };
  }, [resolvedMode]);

  // A theme change is one visual event, so it gets ONE animation: the
  // browser snapshots the page before and after and wipes the new state in
  // over the old with a circular clip (see ::view-transition-new(root) in
  // globals.css). That is not the same as animating the colours — the CSS
  // transitions that used to do that stay suppressed, because ~140 of them
  // finishing at their own speeds is what made the switch look broken.
  //
  // startViewTransition needs the DOM mutated synchronously inside its
  // callback, hence flushSync + writing data-ds-mode directly rather than
  // waiting for the effect above (which still runs and writes the same
  // value — it stays the source of truth for mount and for the fallback).
  const setMode = useCallback(
    (next: ThemeMode) => {
      // Resolve BEFORE the transition: the attribute always carries
      // light/dark, and choosing "system" has to land on whatever the OS
      // says right now rather than on the string "system".
      const nextResolved: ResolvedMode = next === "system" ? getSystemSnapshot() : next;

      // Two reasons to take the plain path: Firefox has no View Transitions
      // yet (the wipe is an enhancement, never a requirement for changing
      // theme), and a full-viewport wipe is exactly the kind of motion
      // someone asking for reduced motion is asking not to see.
      const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
      const reduceMotion =
        typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // Picking a mode that resolves to what is already on screen (System
      // while already dark, say) is a real state change but not a visual
      // one — running the wipe for it would flash the whole viewport for no
      // reason the user can see.
      const currentResolved = document.documentElement.getAttribute("data-ds-mode");
      if (typeof doc.startViewTransition !== "function" || reduceMotion || nextResolved === currentResolved) {
        writeMode(next);
        return;
      }

      doc.startViewTransition(() => {
        doc.documentElement.setAttribute("data-ds-mode", nextResolved);
        flushSync(() => writeMode(next));
      });
    },
    []
  );

  return <ThemeContext.Provider value={{ mode, resolvedMode, setMode }}>{children}</ThemeContext.Provider>;
}

export function useThemeMode(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useThemeMode must be used inside <AppThemeProvider>");
  return ctx;
}
